import { useQuery } from '@tanstack/react-query'
import type { components } from '@akillidernek/api-client'
import { Field, Input } from '@akillidernek/ui'
import { api } from './api'
import { useTenantSlug } from './use-tenant'

export type OzelAlan = components['schemas']['CustomFieldRead']

/** Derneğin AKTİF özel alan tanımları. Gizlenmiş alan forma girmez. */
export function useOzelAlanlar() {
  const slug = useTenantSlug()
  return useQuery({
    queryKey: ['custom-fields', slug, 'active'],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/custom-fields', {
        params: { query: { entity_type: 'person', only_active: true } },
      })
      return data ?? []
    },
  })
}

/**
 * Özel alanları forma çizer — YES-105.
 *
 * Değerler `FormData`'ya `ozel:<kod>` önekiyle giriyor. Önek şart: derneğin
 * tanımladığı bir alanın kodu çekirdek bir alanla çakışabilir ("phone" gibi)
 * ve çakışırsa üyenin gerçek telefonu sessizce özel alan değeriyle ezilirdi.
 */
const ONEK = 'ozel:'

export function ozelAlanDegerleri(
  form: FormData,
  alanlar: readonly OzelAlan[],
): Record<string, unknown> {
  const sonuc: Record<string, unknown> = {}
  for (const alan of alanlar) {
    const anahtar = `${ONEK}${alan.key}`
    if (alan.field_type === 'multi_select') {
      sonuc[alan.key] = form.getAll(anahtar).map(String)
      continue
    }
    if (alan.field_type === 'boolean') {
      // İşaretlenmemiş kutu FormData'ya HİÇ girmez; "hayır" ile "dokunulmadı"
      // ayrımını kaybetmemek için açıkça false yazıyoruz.
      sonuc[alan.key] = form.get(anahtar) !== null
      continue
    }
    sonuc[alan.key] = String(form.get(anahtar) ?? '')
  }
  return sonuc
}

export function OzelAlanlar({
  alanlar,
  degerler,
  hatalar,
}: {
  alanlar: readonly OzelAlan[]
  degerler?: Record<string, unknown>
  hatalar?: Record<string, string>
}) {
  if (alanlar.length === 0) return null

  return (
    <div className="flex flex-col gap-3 border-t border-[var(--ad-border)] pt-3">
      <p className="text-xs font-medium uppercase tracking-wide text-[var(--ad-text-muted)]">
        Derneğe özel alanlar
      </p>
      {alanlar.map((alan) => {
        const ad = `${ONEK}${alan.key}`
        const deger = degerler?.[alan.key]

        if (alan.field_type === 'boolean') {
          return (
            <label key={alan.id} className="flex items-center gap-2 text-sm">
              <input type="checkbox" name={ad} defaultChecked={deger === true} />
              {alan.label}
            </label>
          )
        }

        if (alan.field_type === 'select') {
          return (
            <Field
              key={alan.id}
              label={alan.label}
              required={alan.is_required}
              error={hatalar?.[alan.key]}
            >
              {(props) => (
                <select
                  name={ad}
                  defaultValue={deger === undefined ? '' : String(deger)}
                  className="h-10 w-full rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] px-3 text-sm"
                  {...props}
                >
                  {/* Zorunlu olmayan alanda boş seçenek şart: aksi halde
                      kullanıcı ilk seçeneği seçmek zorunda kalırdı. */}
                  {!alan.is_required && <option value="">—</option>}
                  {alan.options.map((secenek) => (
                    <option key={String(secenek.value)} value={String(secenek.value)}>
                      {String(secenek.label)}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          )
        }

        if (alan.field_type === 'multi_select') {
          const secili = new Set((Array.isArray(deger) ? deger : []).map(String))
          return (
            <fieldset key={alan.id} className="flex flex-col gap-1">
              <legend className="pb-1 text-sm">
                {alan.label}
                {alan.is_required && <span className="text-[var(--ad-danger)]"> *</span>}
              </legend>
              {alan.options.map((secenek) => (
                <label key={String(secenek.value)} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    name={ad}
                    value={String(secenek.value)}
                    defaultChecked={secili.has(String(secenek.value))}
                  />
                  {String(secenek.label)}
                </label>
              ))}
            </fieldset>
          )
        }

        return (
          <Field
            key={alan.id}
            label={alan.label}
            required={alan.is_required}
            error={hatalar?.[alan.key]}
          >
            {(props) => (
              <Input
                name={ad}
                defaultValue={deger === undefined || deger === null ? '' : String(deger)}
                type={alan.field_type === 'date' ? 'date' : 'text'}
                inputMode={alan.field_type === 'number' ? 'decimal' : undefined}
                {...props}
              />
            )}
          </Field>
        )
      })}
    </div>
  )
}
